import ErrorMessage from "@/v2-ui/ErrorMessage"
import LicenseFeatureGroupContentClass from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupContentClass"
import LicenseFeatureGroupComposeSkeleton from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeSkeleton"
import LicenseFeatureGroupLegendTick from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupLegendTick"
import LicenseFeatureGroupSection from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupSection"
import LicenseFeatureGroupTickLegend from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupTickLegend"
import { RULE_VERSUS_PICKS_LEGEND } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import {
  isCategoryRule,
  type FeatureGroupPickedLike,
  type FeatureGroupRuleLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import {
  contentClassesOf,
  grantOf,
  impactOf,
  nounOf,
  type LicenseFeatureGroupContentClass as ContentClass,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.content.utils"
import {
  isPersonLens,
  useFeatureGroupLens,
  type FeatureGroupLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import {
  isClosedForLens,
  objectOfLens,
  reachesThroughLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"
import { useFeatureContentCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.contentCatalog.query"
import { useFeatureGroupRuleOptionsQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.ruleOptions.query"

export type LicenseFeatureGroupContentSectionProps = {
  featureGroupName: string
  /** The rule draft; the composition owns it and writes it on Save. */
  rules: readonly FeatureGroupRuleLike[]
  persistedRules: readonly FeatureGroupRuleLike[]
  isPicked(code: string): boolean
  wasPicked(code: string): boolean
  onRulesChange(rules: readonly FeatureGroupRuleLike[]): void
  onSetPicked(
    features: readonly FeatureGroupPickedLike[],
    picked: boolean,
  ): void
}

/**
 * "Base maps: all 38 for everyone · Map layers: 12 of 41 locked unlocked";
 * through the customer lens "Map layers: only what's listed, 43 reach them".
 */
function summarize(
  classes: readonly ContentClass[],
  isReached: (item: ContentClass["items"][number]) => boolean,
  lens: FeatureGroupLens,
) {
  if (classes.length === 0) return "What the map offers, per package."
  const them = objectOfLens(lens)
  return classes
    .map((contentClass) => {
      const impact = impactOf(contentClass.items, isReached)
      const noun = nounOf(contentClass)
      if (isClosedForLens(lens, contentClass.featureTypeCode)) {
        return `${contentClass.name}: only what's listed, ${impact.granted} of ${impact.total} reach ${them}`
      }
      if (impact.locked === 0) {
        return lens.kind === "customer"
          ? `${contentClass.name}: open, all ${impact.total} reach ${them}`
          : `${contentClass.name}: all ${impact.total} for everyone`
      }
      if (lens.kind === "customer") {
        return `${contentClass.name}: open, ${impact.granted} of ${impact.total} reach ${them}`
      }
      return `${contentClass.name}: ${impact.grantedLocked} of ${impact.locked} package-only ${noun}s granted`
    })
    .join(" · ")
}

// The package's content: what the map offers that a package decides. One
// card per class the app enforces — base maps, map layers (the other
// feature types are not checked by anything a user touches, so they are
// not offered) — granted whole, by category, or item by item in place,
// where a user meets the item.
function LicenseFeatureGroupContentSection(
  props: LicenseFeatureGroupContentSectionProps,
) {
  const {
    featureGroupName,
    rules,
    persistedRules,
    isPicked,
    wasPicked,
    onRulesChange,
    onSetPicked,
  } = props

  const catalog = useFeatureContentCatalogQuery()
  const optionsQuery = useFeatureGroupRuleOptionsQuery()
  const items =
    (catalog.data ?? catalog.previousData)?.getFeatureContentCatalog ?? []
  const options =
    (optionsQuery.data ?? optionsQuery.previousData)
      ?.getFeatureGroupRuleOptions ?? []
  const isLoading =
    (catalog.loading && !catalog.data && !catalog.previousData) ||
    (optionsQuery.loading && !optionsQuery.data && !optionsQuery.previousData)

  const classes = contentClassesOf(items, options)
  const categoryLabels = new Map(
    options
      .filter(isCategoryRule)
      .map((option) => [option.LayerCategoryFeatureCode ?? "", option.Label]),
  )
  const categoryLabelOf = (code: string) => categoryLabels.get(code) ?? code
  const lens = useFeatureGroupLens()
  const isReached = (item: ContentClass["items"][number]) =>
    reachesThroughLens(lens, item, grantOf(item, rules, isPicked) !== null)

  return (
    <LicenseFeatureGroupSection
      title="Content"
      summary={summarize(classes, isReached, lens)}
      legend={
        isPersonLens(lens) ? (
          <>
            <LicenseFeatureGroupLegendTick /> reaches {lens.userName}; a cross
            does not. Hover either for why: free for everyone, a license, the
            customer&apos;s own list, or a seat they hold or lack.
          </>
        ) : lens.kind === "customer" ? (
          <>
            <LicenseFeatureGroupLegendTick /> included already, with the product
            or through a license (hover the tick for which). An empty box is not
            included; tick it to add it for {lens.customerName} only.{" "}
            {RULE_VERSUS_PICKS_LEGEND}
          </>
        ) : (
          <LicenseFeatureGroupTickLegend />
        )
      }
      about={
        isPersonLens(lens) ? (
          <>
            <p>
              What the map offers {lens.userName}: base maps and map layers, the
              two things the app checks a user for, each with the entitlement
              rule&apos;s answer for {lens.userName} alone. Per class the rule
              runs for {lens.customerName} (open, or only what&apos;s listed)
              and then per person, where a seated license names who holds a
              seat.
            </p>
            <p className="mt-2">
              Nothing is changed here. Licenses and add-ons are set on the
              customer panel&apos;s Licenses &amp; add-ons tab; the
              customer&apos;s own list and open or listed classes on its What
              they get tab.
            </p>
          </>
        ) : lens.kind === "customer" ? (
          <>
            <p>
              What the map offers {lens.customerName}: base maps and map layers,
              the two things the app checks a user for. Per class, Open (the
              default) means every item that is free for everyone plus what
              their licenses include, new items arriving on their own; Only
              what&apos;s listed means exactly what their licenses include and
              what is ticked here, by category rule (now and in future releases)
              or item by item.
            </p>
            <p className="mt-2">
              A fixed tick is the product&apos;s or a license&apos;s and cannot
              be changed here; change their licenses on the Licensing tab. A
              checkbox is this tab&apos;s to decide.
            </p>
          </>
        ) : (
          <>
            <p>
              What the map offers that a package decides: base maps and map
              layers, the two things the app checks a license for. Granted as a
              whole class (now and in future releases), by layer category, or
              item by item, where a user meets the item.
            </p>
            <p className="mt-2">
              Only an item with a lock (package-only) depends on packages at
              all; everyone gets the rest, whatever a package says. A tick on a
              free item is a placement: it records that this package keeps the
              item if it is ever made package-only, and changes nothing before
              then.
            </p>
          </>
        )
      }
    >
      <ErrorMessage error={catalog.error ?? optionsQuery.error} />
      {isLoading ? (
        <LicenseFeatureGroupComposeSkeleton columns={2} />
      ) : (
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
          {classes.map((contentClass) => (
            <LicenseFeatureGroupContentClass
              key={contentClass.featureTypeCode}
              contentClass={contentClass}
              categoryLabelOf={categoryLabelOf}
              rules={rules}
              persistedRules={persistedRules}
              featureGroupName={featureGroupName}
              isPicked={isPicked}
              wasPicked={wasPicked}
              onRulesChange={onRulesChange}
              onSetPicked={onSetPicked}
            />
          ))}
        </div>
      )}
    </LicenseFeatureGroupSection>
  )
}

export default LicenseFeatureGroupContentSection
